import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GameMode, GameSkin } from '../types/site';
import './SnakeGame.css';

interface Position {
  x: number;
  y: number;
}

interface Direction extends Position {}

interface SnakeGameProps {
  mode: GameMode;
  skin: GameSkin;
  playerName: string;
  highScore: number;
  onScoreRecorded?: (score: number, durationMs: number) => void;
}

type GameStatus = 'idle' | 'playing' | 'over';

const GRID_SIZE = 20;
const SWIPE_THRESHOLD = 18;
const INITIAL_DIRECTION: Direction = { x: 0, y: -1 };

const DIRECTION_MAP = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 }
} as const;

function createInitialSnake(): Position[] {
  const center = Math.floor(GRID_SIZE / 2);

  return [
    { x: center, y: center },
    { x: center, y: center + 1 },
    { x: center, y: center + 2 }
  ];
}

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const SnakeGame: React.FC<SnakeGameProps> = ({
  mode,
  skin,
  playerName,
  highScore,
  onScoreRecorded
}) => {
  const [snake, setSnake] = useState<Position[]>(() => createInitialSnake());
  const [food, setFood] = useState<Position>({ x: 5, y: 5 });
  const [direction, setDirection] = useState<Direction>(INITIAL_DIRECTION);
  const [nextDirection, setNextDirection] = useState<Direction>(INITIAL_DIRECTION);
  const [score, setScore] = useState(0);
  const [gameStatus, setGameStatus] = useState<GameStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const runStartRef = useRef<number | null>(null);
  const touchStartRef = useRef<Position | null>(null);
  const scoreRecordedRef = useRef(false);

  const generateFood = useCallback((currentSnake: Position[]): Position => {
    const occupiedPositions = new Set(currentSnake.map((segment) => `${segment.x}-${segment.y}`));
    const freeCells: Position[] = [];

    for (let y = 0; y < GRID_SIZE; y += 1) {
      for (let x = 0; x < GRID_SIZE; x += 1) {
        if (!occupiedPositions.has(`${x}-${y}`)) {
          freeCells.push({ x, y });
        }
      }
    }

    if (freeCells.length === 0) {
      return { x: 0, y: 0 };
    }

    return freeCells[Math.floor(Math.random() * freeCells.length)];
  }, []);

  const isValidDirectionChange = useCallback((currentDir: Direction, newDir: Direction): boolean => {
    return !(currentDir.x === -newDir.x && currentDir.y === -newDir.y);
  }, []);

  const getElapsedDuration = useCallback((): number => {
    if (!runStartRef.current) {
      return elapsedMs;
    }

    return Date.now() - runStartRef.current;
  }, [elapsedMs]);

  const resetGame = useCallback(
    (autoStart = false): void => {
      const initialSnake = createInitialSnake();

      setSnake(initialSnake);
      setFood(generateFood(initialSnake));
      setDirection(INITIAL_DIRECTION);
      setNextDirection(INITIAL_DIRECTION);
      setScore(0);
      setElapsedMs(0);
      scoreRecordedRef.current = false;
      runStartRef.current = autoStart ? Date.now() : null;
      setGameStatus(autoStart ? 'playing' : 'idle');
    },
    [generateFood]
  );

  const startGame = useCallback((): void => {
    if (gameStatus === 'playing') {
      return;
    }

    if (gameStatus === 'over') {
      resetGame(true);
      return;
    }

    scoreRecordedRef.current = false;
    runStartRef.current = Date.now();
    setElapsedMs(0);
    setGameStatus('playing');
  }, [gameStatus, resetGame]);

  const handleDirectionInput = useCallback(
    (newDirection: Direction): void => {
      if (gameStatus === 'over') {
        return;
      }

      if (isValidDirectionChange(direction, newDirection)) {
        setNextDirection(newDirection);
      }

      if (gameStatus === 'idle') {
        startGame();
      }
    },
    [direction, gameStatus, isValidDirectionChange, startGame]
  );

  const moveSnake = useCallback((): void => {
    if (gameStatus !== 'playing') {
      return;
    }

    setSnake((previousSnake) => {
      const activeDirection = isValidDirectionChange(direction, nextDirection)
        ? nextDirection
        : direction;

      if (activeDirection.x !== direction.x || activeDirection.y !== direction.y) {
        setDirection(activeDirection);
      }

      let nextHead: Position = {
        x: previousSnake[0].x + activeDirection.x,
        y: previousSnake[0].y + activeDirection.y
      };

      if (mode.wallBehavior === 'wrap') {
        nextHead = {
          x: (nextHead.x + GRID_SIZE) % GRID_SIZE,
          y: (nextHead.y + GRID_SIZE) % GRID_SIZE
        };
      } else if (
        nextHead.x < 0 ||
        nextHead.x >= GRID_SIZE ||
        nextHead.y < 0 ||
        nextHead.y >= GRID_SIZE
      ) {
        setElapsedMs(getElapsedDuration());
        setGameStatus('over');
        return previousSnake;
      }

      const ateFood = nextHead.x === food.x && nextHead.y === food.y;
      const collisionSegments = ateFood ? previousSnake : previousSnake.slice(0, -1);

      if (
        collisionSegments.some((segment) => segment.x === nextHead.x && segment.y === nextHead.y)
      ) {
        setElapsedMs(getElapsedDuration());
        setGameStatus('over');
        return previousSnake;
      }

      const nextSnake = [nextHead, ...previousSnake];

      if (ateFood) {
        setScore((currentScore) => currentScore + 1);
        setFood(generateFood(nextSnake));
      } else {
        nextSnake.pop();
      }

      return nextSnake;
    });
  }, [direction, food, gameStatus, generateFood, getElapsedDuration, isValidDirectionChange, mode.wallBehavior, nextDirection]);

  useEffect(() => {
    resetGame();
  }, [mode.id, resetGame]);

  useEffect(() => {
    if (gameStatus !== 'playing') {
      return undefined;
    }

    const gameInterval = window.setInterval(moveSnake, mode.speed);

    return () => window.clearInterval(gameInterval);
  }, [gameStatus, mode.speed, moveSnake]);

  useEffect(() => {
    if (gameStatus !== 'playing') {
      return undefined;
    }

    const timerInterval = window.setInterval(() => {
      if (runStartRef.current) {
        setElapsedMs(Date.now() - runStartRef.current);
      }
    }, 1000);

    return () => window.clearInterval(timerInterval);
  }, [gameStatus]);

  useEffect(() => {
    if (gameStatus !== 'over' || scoreRecordedRef.current || score <= 0) {
      return;
    }

    const duration = getElapsedDuration();
    scoreRecordedRef.current = true;
    setElapsedMs(duration);
    onScoreRecorded?.(score, duration);
  }, [gameStatus, getElapsedDuration, onScoreRecorded, score]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;

      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      if (gameStatus === 'over' && key === 'enter') {
        event.preventDefault();
        resetGame(true);
        return;
      }

      if (key === ' ' && gameStatus === 'idle') {
        event.preventDefault();
        startGame();
        return;
      }

      const keyMap: Record<string, Direction | undefined> = {
        arrowup: DIRECTION_MAP.up,
        arrowdown: DIRECTION_MAP.down,
        arrowleft: DIRECTION_MAP.left,
        arrowright: DIRECTION_MAP.right,
        w: DIRECTION_MAP.up,
        a: DIRECTION_MAP.left,
        s: DIRECTION_MAP.down,
        d: DIRECTION_MAP.right
      };

      const mappedDirection = keyMap[key];

      if (!mappedDirection) {
        return;
      }

      event.preventDefault();
      handleDirectionInput(mappedDirection);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [gameStatus, handleDirectionInput, resetGame, startGame]);

  const skinVariables = useMemo(
    () =>
      ({
        '--game-accent': skin.palette.accent,
        '--game-accent-soft': skin.palette.accentSoft,
        '--game-panel': skin.palette.panel,
        '--game-border': skin.palette.border,
        '--game-board': skin.palette.board,
        '--game-cell': skin.palette.cell,
        '--game-snake': skin.palette.snake,
        '--game-snake-alt': skin.palette.snakeAlt,
        '--game-head': skin.palette.head,
        '--game-food': skin.palette.food,
        '--game-food-glow': skin.palette.foodGlow,
        '--game-text': skin.palette.text,
        '--game-muted': skin.palette.muted,
        '--game-control': skin.palette.control,
        '--game-control-shadow': skin.palette.controlShadow,
        '--grid-size': String(GRID_SIZE)
      } as React.CSSProperties),
    [skin]
  );

  const boardLabel =
    gameStatus === 'idle'
      ? 'Ready to play'
      : gameStatus === 'playing'
        ? 'Live run in progress'
        : 'Run finished';

  return (
    <section className="snake-game" style={skinVariables}>
      <header className="game-header">
        <div>
          <p className="game-header__eyebrow">{mode.name}</p>
          <h2>{playerName}&apos;s board</h2>
          <p className="game-header__copy">{mode.description}</p>
        </div>

        <div className="game-header__stats">
          <article className="game-stat">
            <span>Score</span>
            <strong>{score}</strong>
          </article>
          <article className="game-stat">
            <span>Mode best</span>
            <strong>{highScore}</strong>
          </article>
          <article className="game-stat">
            <span>Run time</span>
            <strong>{formatDuration(elapsedMs)}</strong>
          </article>
        </div>
      </header>

      <div className="game-shell">
        <div
          className="game-container"
          onTouchStart={(event) => {
            const touch = event.touches[0];
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
          }}
          onTouchEnd={(event) => {
            const startPoint = touchStartRef.current;
            touchStartRef.current = null;

            if (!startPoint) {
              return;
            }

            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - startPoint.x;
            const deltaY = touch.clientY - startPoint.y;
            const absX = Math.abs(deltaX);
            const absY = Math.abs(deltaY);

            if (absX < SWIPE_THRESHOLD && absY < SWIPE_THRESHOLD) {
              if (gameStatus === 'idle') {
                startGame();
              }
              return;
            }

            if (absX > absY) {
              handleDirectionInput(deltaX > 0 ? DIRECTION_MAP.right : DIRECTION_MAP.left);
            } else {
              handleDirectionInput(deltaY > 0 ? DIRECTION_MAP.down : DIRECTION_MAP.up);
            }
          }}
          onClick={() => {
            if (gameStatus === 'idle') {
              startGame();
            }
          }}
        >
          <div className="game-board" aria-label={boardLabel}>
            {Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, index) => {
              const x = index % GRID_SIZE;
              const y = Math.floor(index / GRID_SIZE);
              const isFood = food.x === x && food.y === y;
              const isHead = snake[0]?.x === x && snake[0]?.y === y;
              const isSnake = snake.some((segment) => segment.x === x && segment.y === y);

              return (
                <div
                  key={`${x}-${y}`}
                  className={[
                    'cell',
                    isSnake ? 'cell--snake' : '',
                    isHead ? 'cell--head' : '',
                    isFood ? 'cell--food' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              );
            })}
          </div>

          {gameStatus === 'idle' ? (
            <div className="game-overlay">
              <div className="game-dialog">
                <span className="site-chip">Ready</span>
                <h3>Tap, swipe, or press a key to start.</h3>
                <p>Use swipe gestures or the D-pad on mobile, and arrows or WASD on desktop.</p>
                <button type="button" className="button button--primary" onClick={startGame}>
                  Start run
                </button>
              </div>
            </div>
          ) : null}

          {gameStatus === 'over' ? (
            <div className="game-overlay">
              <div className="game-dialog">
                <span className="site-chip">Run complete</span>
                <h3>Game over.</h3>
                <p>
                  Score {score} in {formatDuration(elapsedMs)}. Press Enter on desktop or tap the
                  button to jump back in.
                </p>
                <button type="button" className="button button--primary" onClick={() => resetGame(true)}>
                  Play again
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <aside className="game-sidepanel">
          <div className="game-status-panel">
            <span className="site-chip">{skin.name}</span>
            <span className="site-chip">{boardLabel}</span>
          </div>

          <div className="touch-controls" aria-label="Touch controls">
            <button
              type="button"
              className="touch-button touch-button--up"
              aria-label="Move up"
              onClick={() => handleDirectionInput(DIRECTION_MAP.up)}
            >
              ↑
            </button>
            <button
              type="button"
              className="touch-button touch-button--left"
              aria-label="Move left"
              onClick={() => handleDirectionInput(DIRECTION_MAP.left)}
            >
              ←
            </button>
            <button
              type="button"
              className="touch-button touch-button--right"
              aria-label="Move right"
              onClick={() => handleDirectionInput(DIRECTION_MAP.right)}
            >
              →
            </button>
            <button
              type="button"
              className="touch-button touch-button--down"
              aria-label="Move down"
              onClick={() => handleDirectionInput(DIRECTION_MAP.down)}
            >
              ↓
            </button>
          </div>

          <div className="controls">
            <p>Walls: {mode.wallBehavior === 'wrap' ? 'Wrap to opposite side' : 'Collide on touch'}</p>
            <p>Controls: Tap start, swipe board, or use arrows / WASD.</p>
            <p>Scores save locally after each run over zero points.</p>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default SnakeGame;